import React, { useCallback } from 'react';

import _ from 'lodash';
import { Avatar, Chip, useTheme } from '@material-ui/core';
import { PersonOutlineOutlined } from '@material-ui/icons';

import API from 'utils/api';
import { useSources } from 'hooks';
import { SectionTitle, Table } from 'components';
import { Status } from './helpers';
import Lock from './Lock';

import styles from './Participants.module.scss';

const Participants = ({ drawId, status }) => {
  const theme = useTheme();
  const sources = useSources();

  const highlight = { fontWeight: 'bold', color: theme.palette.primary.main };

  const fetchData = useCallback(
    ({ filter, ...rest }) =>
      drawId
        ? API.getDrawParticipants(drawId, _.merge(rest, filter), sources)
        : Promise.resolve([]),
    [sources, drawId]
  );

  const renderTitle = () => (
    <div className={styles.header}>
      <SectionTitle title="Participants">
        {status === Status.idle ? (
          <>
            Click on the <span style={highlight}>OPEN</span> button to start issuing
            tickets. All tickets and their holders will appear on this panel
          </>
        ) : status === Status.open ? (
          <>
            This panel shows all issued tickets and their holders. When ready, click the{' '}
            <span style={highlight}>CLOSE</span> button to end ticketing and prepare for
            the next phase
          </>
        ) : (
          <>This panel shows all issued tickets and their holders</>
        )}
      </SectionTitle>
    </div>
  );

  const renderUser = ({ name, photo }) => (
    <div className={styles.user}>
      <Avatar className={styles.avatar} src={photo}>
        <PersonOutlineOutlined />
      </Avatar>

      {_.trim(name || 'Anonymous user')
        .replace(/\s+/g, ' ')
        .split(' ')
        .map(it => `${_.toUpper(it.charAt(0))}${it.substring(1)}`)
        .join(' ')}
    </div>
  );

  const renderTickets = ({ tickets }, filter) => {
    const symbols = (filter.tickets || '').split('');

    return (
      <div>
        {_.map(tickets, code => (
          <Chip
            classes={{ label: styles.mono }}
            key={code}
            label={code}
            size="small"
            variant="outlined"
            color={
              symbols.length && symbols.every(it => code.includes(_.toUpper(it)))
                ? 'primary'
                : 'default'
            }
          />
        ))}
      </div>
    );
  };

  return (
    <div className={styles.participants}>
      {status === Status.idle ? (
        <>
          {renderTitle()}
          <Lock label="When ready, click on the OPEN button above to start issuing tickets" />
        </>
      ) : (
        <Table
          title={renderTitle()}
          topBarStyle={{ padding: 0 }}
          data={fetchData}
          filtering
          refreshButton
          search={false}
          columns={[
            { title: 'User', field: 'name', render: renderUser, defaultSort: 'asc' },
            { title: 'Tickets', field: 'tickets', render: renderTickets, sorting: false },
          ]}
        />
      )}
    </div>
  );
};

export default Participants;
